import React, { useState } from 'react';
import {
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { SESSION_STATUSES } from '../utils/reconConstants';
import { formatReconTimestamp } from '../utils/formatters';
import UploadErrorsDialog from './UploadErrorsDialog';

const STATUS_STYLES = {
  [SESSION_STATUSES.PROCESSING]: { bg: '#FEF9C3', text: '#854D0E' },
  [SESSION_STATUSES.COMPLETED]: { bg: '#DCFCE7', text: '#166534' },
  [SESSION_STATUSES.PARTIALLY_FAILED]: { bg: '#FEF3C7', text: '#92400E' },
  [SESSION_STATUSES.FAILED]: { bg: '#FEE2E2', text: '#991B1B' },
};

const headerSx = { fontSize: 12, fontWeight: 600, color: 'text.secondary', bgcolor: 'rgba(0,0,0,0.03)' };
const cellSx = { fontSize: 12, fontVariantNumeric: 'tabular-nums' };

// Design-reference "Batch processing log": header strip + dense table with
// batch id, timestamps, record counts, failed rows, status chip and actions
// (error log + jump to reporting).
const UploadSessionsTable = ({ sessions, loading, totalSessions, onGoToReporting }) => {
  const { reconciliation: r } = useTranslatedMessages();
  const t = r.batch ?? {};

  const [errorsSession, setErrorsSession] = useState(null);

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'rgba(0,0,0,0.02)' }}
      >
        <Stack>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t.title ?? 'Batch processing log'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t.subtitle ?? 'Async ingestion runs for your account.'}
          </Typography>
        </Stack>
        <Chip size="small" label={`${totalSessions} ${t.runs ?? 'runs'}`} sx={{ height: 20, fontSize: 10 }} />
      </Stack>

      <Table size="small" sx={{ minWidth: 760 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headerSx}>{t.batch_id ?? 'Batch ID'}</TableCell>
            <TableCell sx={headerSx}>{t.uploaded_at ?? 'Upload Date & Time'}</TableCell>
            <TableCell sx={headerSx}>{t.filename ?? 'Filename'}</TableCell>
            <TableCell sx={headerSx} align="right">
              {t.records ?? 'Records'}
            </TableCell>
            <TableCell sx={headerSx}>{t.failed_rows ?? 'Failed Rows'}</TableCell>
            <TableCell sx={headerSx}>{t.status ?? 'Status'}</TableCell>
            <TableCell sx={headerSx} align="right">
              {t.actions ?? 'Actions'}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading && sessions.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary', fontSize: 13 }}>
                {t.empty ?? 'No uploads yet — drop an ERP export to get started.'}
              </TableCell>
            </TableRow>
          )}
          {sessions.map((session) => {
            const status = STATUS_STYLES[session.status] ?? {};
            const processing = session.status === SESSION_STATUSES.PROCESSING;
            const statusLabel = r.batch?.session_status?.[session.status] ?? session.status;
            return (
              <TableRow key={session.upload_session_id} hover>
                <TableCell sx={{ ...cellSx, fontFamily: 'monospace' }}>
                  {session.upload_session_id.slice(0, 8)}
                </TableCell>
                <TableCell sx={{ ...cellSx, color: 'text.secondary' }}>
                  {formatReconTimestamp(session.created_at)}
                </TableCell>
                <TableCell sx={{ ...cellSx, maxWidth: 240 }}>
                  <Typography variant="caption" noWrap title={session.file_name} sx={{ display: 'block' }}>
                    {session.file_name}
                  </Typography>
                </TableCell>
                <TableCell sx={cellSx} align="right">
                  {processing
                    ? `${session.progress_rows ?? 0}/${session.total_rows_received || '…'}`
                    : Number(session.rows_inserted ?? 0).toLocaleString()}
                </TableCell>
                <TableCell sx={cellSx}>
                  {session.rows_failed > 0 ? (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: '#DC2626' }}>
                      <ErrorOutlineOutlinedIcon sx={{ fontSize: 13 }} />
                      <span>{session.rows_failed}</span>
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      0
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={cellSx}>
                  <Chip
                    size="small"
                    label={statusLabel}
                    sx={{ height: 20, fontSize: 11, bgcolor: status.bg, color: status.text, fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip
                    title={
                      session.rows_failed > 0 ? (t.view_errors ?? 'View error log') : (t.no_errors ?? 'No failed rows')
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={!session.rows_failed}
                        onClick={() => setErrorsSession(session)}
                      >
                        <DescriptionOutlinedIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t.go_reporting ?? 'Go to reporting'}>
                    <IconButton size="small" onClick={onGoToReporting}>
                      <ArrowForwardOutlinedIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <UploadErrorsDialog
        session={errorsSession}
        open={Boolean(errorsSession)}
        onClose={() => setErrorsSession(null)}
      />
    </Paper>
  );
};

export default UploadSessionsTable;
